import React, { Fragment, useContext, useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { Alert as Alert2, Button, Col, Container, Row, Table } from "reactstrap";
import _ from "lodash";
import {
  FilterDateTime,
  FilterMultiSelect,
  FilterSelect,
  ItemDropdownMenu,
  ItemTypeahead,
  ServiceThreadSpinner,
  SortHeader
} from "./";
import { api, constants, dateHelpers, filterHelpers, helpers, UserContext } from "../utils";
import { toast } from "react-toastify";

export default function MostSold(props) {
  const { currentUser } = useContext(UserContext);
  const firstRender = useRef(true);
  const startDate = moment().subtract(3, "months").startOf("month");
  const endDate = moment().subtract(1, "months").endOf("month");

  const [changedHeaders, setChangedHeaders] = useState(false);
  const [classList, setClassList] = useState([]);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState([
    { filterName: "classList", value: [] },
    { filterName: "StartDate", value: startDate },
    { filterName: "EndDate", value: endDate },
  ]);
  const [initialized, setInitialized] = useState(false);
  const [itemNumber, setItemNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortField, setSortField] = useState("currentSales");
  const [totals, setTotals] = useState({
    TotalSold: 0,
    TotalSales: 0
  });
  const [typeOfReport, setTypeOfReport] = useState({ value: 3, label: "Most Sold Summary View" });

  useEffect(() => {
    setData(null);
    let changedSomething = false;
    let newFilters = filters.slice();
    if (changedSomething) {
      setFilters(newFilters);
    }
  }, [filters]);

  useEffect(() => {
    if (initialized) return;
    if (currentUser && !initialized) {
      if (currentUser.customerFilter) {
        currentUser.setCustomerFilter(null);
      }
    }
    let apiCalls = [];
    apiCalls.push(
      api
        .fetch(`Reference/GetSalespeopleList`)
        .then((response) => {
          return {
            salespeople: response.data.salespeople,
          };
        })
        .catch(api.catchHandler)
    );
    apiCalls.push(
      api
        .fetch(`Reference/GetItemClasses`)
        .then((response) => {
          return {
            itemClasses: _.map(response.data.itemClasses, (x) => {
              return {
                value: x.itemClass,
                label: x.itemClassWithDesc,
              };
            }),
          };
        })
        .catch(api.catchHandler)
    );
    Promise.all(apiCalls)
      .then((arrayResults) => {
        let aggResults = {};
        _.each(arrayResults, (x) => Object.assign(aggResults, x));
        setClassList(aggResults.itemClasses);
      })
      .catch((error) =>
        setMessage({
          flavor: "alert-danger",
          text: "There was an error loading sales people data",
        })
      )
      .finally(() => setInitialized(true));
  }, [initialized, currentUser]);

  useEffect(() => {
    // have this variable watch when the user changes the Report options
    // if they change the report option then set it to true so that the
    // data is hidden until the user clicks on view sales again
    setChangedHeaders(true);
  }, [typeOfReport])

  useEffect(() => {
    if (!initialized) return;
    if (firstRender.current) {
      firstRender.current = false;
      return;
    } else {
      runQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection, initialized]);

  function createPayload() {
    let payload = {
      SortField: sortField,
      SortDirection: sortDirection,
      TypeOfMostSoldReport: typeOfReport.value,
    };
    if (itemNumber) {
      payload.itemNumber = itemNumber.itemNumber;
    }
    _.map(filters, (filter) => {
      payload[filter.filterName] = filter.value?.value
        ? filter.value?.value
        : filter.value;
    });
    payload.StartDate = dateHelpers.formatDateForServer(payload.StartDate);
    payload.EndDate = dateHelpers.formatDateForServer(payload.EndDate);
    if (filterHelpers.getValue(filters, "classList").length === 0) {
      payload.classList = _.map(classList, (x) => x.value);
    }
    if (payload.selectedItem) {
      payload.itemNumber = payload.selectedItem.itemNumber;
      delete payload.selectedItem;
    }
    return payload;
  }

  function currentDate() {
    let date = new Date();
    let month = date.getMonth() + 1;
    let day = date.getDate();
    let year = date.getFullYear();
    return `${month}-${day}-${year}`;
  }

  function ExportToXLSX() {
    let payload = createPayload();
    api.postGetBlob("Report/MostSoldReportXLSX", payload, "Failed to render MostSold xlsx.")
      .then(r => {
        if (!r.data) {
          toast.error(r.message);
        } else {
          const XLSX_FILENAME = getFilename()
          helpers.downloadExport(r.data, XLSX_FILENAME, constants.MIME_TYPES.xlsx);
        }
      })
  }

  function getFilename() {
    if (typeOfReport.value === 1)
      return `MostSold_${currentDate()}.xlsx`;
    else if (typeOfReport.value === 2)
      return `MostSoldMonthly_${currentDate()}.xlsx`;
    else
      return `MostSoldSummary_${currentDate()}.xlsx`;
  }

  function onDownloadMostSoldReport() {
    setLoading(true);
    api
      .postDownloadBinary(
        "Report/MostSoldReport",
        createPayload(),
        constants.MIME_TYPES.pdf,
        `MostSoldReport_${currentDate()}.pdf`,
        "There was an error downloading the Most Sold Report pdf."
      )
      .then((res) => {
        if (res?.data === false) {
          toast.error(res.message);
        }
      })
      .catch(api.catchHandler)
      .finally(() => setLoading(false));
  }

  function onFilterChange(changedFilter) {
    const DATE_FILTER_NAMES = [
      "StartDate",
      "EndDate",
      "PreviousStartDate",
      "PreviousEndDate",
    ];
    if (DATE_FILTER_NAMES.includes(changedFilter.filterName)) {
      changedFilter.value = moment(changedFilter.value);
    }
    const filterChanges = filterHelpers.getFilters(filters, changedFilter);
    setFilters(filterChanges);
  }

  function onSortChange(field, direction) {
    setSortField(field);
    setSortDirection(direction);
  }

  function runQuery() {
    setLoading(true);
    const payload = createPayload();
    api
      .post(`Customer/MostSoldReport`, payload)
      .then((response) => {
        if (response.data.success) {
          let sortedData = _.orderBy(
            response.data.newOrdersData,
            [sortField],
            [sortDirection]
          );
          /*
              if most sold report is one of these we must blank out duplicate values in rows for the UI
                { value: 1, label: "Most Sold W/Order Breakdown" },
                { value: 2, label: "Most Sold W/Monthly Breakdown" },
              */
          setChangedHeaders(false);
          if (
            payload.TypeOfMostSoldReport === 1 ||
            payload.TypeOfMostSoldReport === 2
          ) {
            let lastItem = "";
            let lastItem2 = null;
            for (var i = 0; i < sortedData.length; i++) {
              let item = sortedData[i];
              if (
                item.itemWithDesc === lastItem ||
                item.itemWithDesc === lastItem2
              ) {
                item.itemWithDesc = "";
                item.itemClass = "";
                item.monthlyUsageAverage = null;
                item.monthlySalesAverage = null;
                item.itemGroupTotalQuantity = "";
                item.itemGroupTotalSales = null;
                item.quantityOnHand = "";
              } else {
                lastItem = item.itemWithDesc;
              }
            }
          }

          let totalSales = _.sumBy(response.data.newOrdersData, x => {
            if (x.itemGroupTotalSales) return x.itemGroupTotalSales
          });
          let totalSold = _.sumBy(response.data.newOrdersData, x => {
            if (x.itemGroupTotalQuantity) return x.itemGroupTotalQuantity
          });

          setTotals({
            TotalSales: totalSales,
            TotalSold: totalSold
          })
          setData({
            ...response.data.newOrdersData,
            items: sortedData,
          });
        } else {
          setMessage({
            flavor: "alert-danger",
            text: "There was an error loading data: " + response.data.message,
          });
        }
      })
      .catch(api.catchHandler)
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <Container fluid>
      <Row>
        <Col>
          <Fragment>
            {message ? (
              <Row className="mt-3">
                <Col>
                  <Alert2 className={message.flavor}>{message.text}</Alert2>
                </Col>
              </Row>
            ) : null}
            <Row
              className="justify-content-center"
              style={{ marginBottom: "30px" }}
            >
              <Col xxl={{ offset: 0, size: 12 }}>
                <Row className="align-items-center my-3">
                  <Col className="text-end  h5" xs={3}>
                    Item #:
                  </Col>
                  <Col xs={8}>
                    <ItemTypeahead
                      value={itemNumber}
                      isClearable
                      onChange={(selectedItem) => {
                        setItemNumber(selectedItem);
                      }}
                    />
                  </Col>
                </Row>
                <Row className="align-items-center my-1 mb-3">
                  <Col className="text-end  h5" xs={3}>
                    Classes:
                  </Col>
                  <Col xs={8}>
                    <FilterMultiSelect
                      filterName="classList"
                      options={classList}
                      values={filterHelpers.getValue(filters, "classList")}
                      onChangeCallback={(e) => onFilterChange(e)}
                    />
                  </Col>
                </Row>
                <Row className="my-2 mb-3">
                  <Col xs={3}>
                    <h5 children="Start Date:" className="float-end " />
                  </Col>
                  <Col xs={3}>
                    <FilterDateTime
                      filterName="StartDate"
                      value={dateHelpers.formatDateForServer(
                        filterHelpers.getValue(filters, "StartDate")
                      )}
                      onChangeCallback={(e) => onFilterChange(e)}
                    />
                  </Col>
                </Row>
                <Row className="my-2 mb-3">
                  <Col xs={3}>
                    <h5 children="End Date:" className="float-end " />
                  </Col>
                  <Col xs={3}>
                    <FilterDateTime
                      filterName="EndDate"
                      value={dateHelpers.formatDateForServer(
                        filterHelpers.getValue(filters, "EndDate")
                      )}
                      onChangeCallback={(e) => onFilterChange(e)}
                    />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col xs={3}>
                    <h5
                      children="Report Options:"
                      className="float-end "
                    />
                  </Col>
                  <Col xs={5}>
                    <FilterSelect
                      filterName="TypeOfMostSoldReport"
                      options={constants.MOST_SOLD_REPORT_TYPES}
                      value={typeOfReport}
                      onChangeCallback={(e) => {
                        setTypeOfReport(e);
                      }}
                    />
                  </Col>
                </Row>
                <Row
                  className="mb-3"
                >
                  <Col xxl={{ offset: 3, size: 6 }}>
                    <Button
                      title="View Sales"
                      className="shredButton float-start"
                      onClick={runQuery}
                    >
                      View Sales
                    </Button>
                    <Button
                      title="Print Most Sold Report"
                      className="shredButton float-start ms-2"
                      onClick={onDownloadMostSoldReport}
                    >
                      Print Report
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
            <ServiceThreadSpinner on={loading}>
              {!changedHeaders && data && data.items.length && typeOfReport.value === 3 ? (
                <Row className="px-2 py-2" style={{ marginTop: 20 }}>
                  <Col xxl={{ offset: 0, size: 12 }}>
                    <Row className={"mb-2"}>
                      <Col
                        xs={{ offset: 6, size: 6 }}
                        className="transparent-background"
                      >
                        <Button
                          title="Download as .xlsx"
                          className="shredButtonExcel float-end ms-2"
                          onClick={() => ExportToXLSX()}
                        // disabled={working}
                        >
                          <FontAwesomeIcon icon="file-excel" size="lg" />
                        </Button>
                      </Col>
                    </Row>
                    <Table striped className="data-table">
                      <thead className="table-header">
                        <tr>
                          <th>
                            <SortHeader
                              displayName="Item"
                              field="itemNumber"
                              sortDirection={sortDirection}
                              sorted={sortField === "itemNumber"}
                              callback={onSortChange}
                            />
                          </th>
                          <th style={{ textAlign: "center" }}>
                            <SortHeader
                              displayName="Class"
                              field="itemClass"
                              sortDirection={sortDirection}
                              sorted={sortField === "itemClass"}
                              callback={onSortChange}
                            />
                          </th>
                          <th style={{ textAlign: "center" }}>
                            <SortHeader
                              displayName="Avg Usage"
                              field="monthlyUsageAverage"
                              sortDirection={sortDirection}
                              sorted={sortField === "monthlyUsageAverage"}
                              callback={onSortChange}
                            />
                          </th>
                          <th style={{ textAlign: "center" }}>
                            <SortHeader
                              displayName="Sales Average"
                              field="monthlySalesAverage"
                              sortDirection={sortDirection}
                              sorted={sortField === "monthlySalesAverage"}
                              callback={onSortChange}
                            />
                          </th>
                          <th style={{ textAlign: "center" }}>
                            <SortHeader
                              displayName="Total Sold"
                              field="itemGroupTotalQuantity"
                              sortDirection={sortDirection}
                              sorted={sortField === "itemGroupTotalQuantity"}
                              callback={onSortChange}
                            />
                          </th>
                          <th style={{ textAlign: "center" }}>
                            <SortHeader
                              displayName="Total Sales"
                              field="itemGroupTotalSales"
                              sortDirection={sortDirection}
                              sorted={sortField === "itemGroupTotalSales"}
                              callback={onSortChange}
                            />
                          </th>
                          <th style={{ textAlign: "center" }}>
                            <SortHeader
                              displayName="On Hand"
                              field="quantityOnHand"
                              sortDirection={sortDirection}
                              sorted={sortField === "quantityOnHand"}
                              callback={onSortChange}
                            />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {_.map(data.items, (datum, index) => {
                          return (
                            <tr key={index}>
                              <ItemDropdownMenu
                                itemNumber={datum.itemNumber}
                                itemDescription={datum.itemWithDesc}
                                attachmentCount={datum.itemAttachmentCount}
                              />
                              <td
                                className="data-cell"
                                children={datum.itemClass}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={helpers.formatNumberWithThousands(helpers.roundToNearestWholeNumber(datum.monthlyUsageAverage))}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={helpers.formatCurrency(
                                  datum.monthlySalesAverage
                                )}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={helpers.formatNumberWithThousands(datum.itemGroupTotalQuantity)}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={helpers.formatCurrency(
                                  datum.itemGroupTotalSales
                                )}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={helpers.formatNumberWithThousands(datum.quantityOnHand)}
                                style={{ textAlign: "center" }}
                              />
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="table-footer text-center">
                        <tr>
                          <td colSpan={4}></td>
                          <td className="text-bold">{helpers.formatNumberWithThousands(totals.TotalSold)}</td>
                          <td className="text-bold">{helpers.formatCurrency(totals.TotalSales)}</td>
                          <td colSpan={1}></td>
                        </tr>
                      </tfoot>
                    </Table>
                  </Col>
                </Row>
              ) : typeOfReport.value === 3 ? <span style={{ display: 'block', textAlign: 'center', fontSize: '1.2em' }}>{firstRender ? "Please select some criteria to see results" : "No Data Found"}</span> : false}
              {!changedHeaders && data && data.items.length && typeOfReport.value === 2 ? (
                <Row className="px-2 py-2" style={{ marginTop: 20 }}>
                  <Row className={"mb-2"}>
                    <Col
                      xs={{ offset: 6, size: 6 }}
                      className="transparent-background"
                    >
                      <Button
                        title="Download as .xlsx"
                        className="shredButtonExcel float-end ms-2"
                        onClick={() => ExportToXLSX()}
                      // disabled={working}
                      >
                        <FontAwesomeIcon icon="file-excel" size="lg" />
                      </Button>
                    </Col>
                  </Row>
                  <Table striped className="data-table">
                    <thead className="table-header">
                      <tr>
                        <th>
                          <SortHeader
                            displayName="Item"
                            field="itemWithDesc"
                            sortDirection={sortDirection}
                            sorted={sortField === "itemWithDesc"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Class"
                            field="itemClass"
                            sortDirection={sortDirection}
                            sorted={sortField === "itemClass"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Avg Usage"
                            field="monthlyUsageAverage"
                            sortDirection={sortDirection}
                            sorted={sortField === "monthlyUsageAverage"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Sales Average"
                            field="monthlySalesAverage"
                            sortDirection={sortDirection}
                            sorted={sortField === "monthlySalesAverage"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Total Sold"
                            field="itemGroupTotalQuantity"
                            sortDirection={sortDirection}
                            sorted={sortField === "itemGroupTotalQuantity"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Total Sales"
                            field="itemGroupTotalSales"
                            sortDirection={sortDirection}
                            sorted={sortField === "itemGroupTotalSales"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="On Hand"
                            field="quantityOnHand"
                            sortDirection={sortDirection}
                            sorted={sortField === "quantityOnHand"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Sales Period"
                            field="orderDate"
                            sortDirection={sortDirection}
                            sorted={sortField === "orderDate"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Monthly Quantity"
                            field="monthlyQuantity"
                            sortDirection={sortDirection}
                            sorted={sortField === "monthlyQuantity"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Monthly Sales"
                            field="monthlySales"
                            sortDirection={sortDirection}
                            sorted={sortField === "monthlySales"}
                            callback={onSortChange}
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {_.map(data.items, (datum, index) => {
                        return (
                          <tr key={index}>
                            <ItemDropdownMenu
                              itemNumber={datum.itemNumber}
                              itemDescription={datum.itemWithDesc}
                              attachmentCount={datum.itemAttachmentCount}
                            />
                            <td
                              className="data-cell"
                              children={datum.itemClass}
                              style={{ textAlign: "center" }}
                            />
                            <td
                              className="data-cell"
                              children={helpers.formatNumberWithThousands(helpers.roundToNearestWholeNumber(datum.monthlyUsageAverage))}
                              style={{ textAlign: "center" }}
                            />
                            <td
                              className="data-cell"
                              children={helpers.formatCurrency(
                                datum.monthlySalesAverage
                              )}
                              style={{ textAlign: "center" }}
                            />
                            <td
                              className="data-cell"
                              children={helpers.formatNumberWithThousands(datum.itemGroupTotalQuantity)}
                              style={{ textAlign: "center" }}
                            />
                            <td
                              className="data-cell"
                              children={helpers.formatCurrency(
                                datum.itemGroupTotalSales
                              )}
                              style={{ textAlign: "center" }}
                            />
                            <td
                              className="data-cell"
                              children={helpers.formatNumberWithThousands(datum.quantityOnHand)}
                              style={{ textAlign: "center" }}
                            />
                            <td
                              className="data-cell"
                              children={datum.monthYearDescription}
                              style={{ textAlign: "center" }}
                            />
                            <td
                              className="data-cell"
                              children={helpers.formatNumberWithThousands(datum.monthlyQuantity)}
                              style={{ textAlign: "center" }}
                            />
                            <td
                              className="data-cell"
                              children={helpers.formatCurrency(datum.monthlySales)}
                              style={{ textAlign: "center" }}
                            />
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="table-footer text-center">
                      <tr>
                        <td colSpan={4}></td>
                        <td className="text-bold">{helpers.formatNumberWithThousands(totals.TotalSold)}</td>
                        <td className="text-bold">{helpers.formatCurrency(totals.TotalSales)}</td>
                        <td colSpan={4}></td>
                      </tr>
                    </tfoot>
                  </Table>
                </Row>
              ) : typeOfReport.value === 2 ? <span style={{ display: 'block', textAlign: 'center', fontSize: '1.2em' }}>{firstRender ? "Please select some criteria to see results" : "No Data Found"}</span> : false}
              {!changedHeaders && data && data.items.length && typeOfReport.value === 1 ? (
                <Row className="px-2 py-2" style={{ marginTop: 20 }}>
                  <Row className={"mb-2"}>
                    <Col
                      xs={{ offset: 6, size: 6 }}
                      className="transparent-background"
                    >
                      <Button
                        title="Download as .xlsx"
                        className="shredButtonExcel float-end ms-2"
                        onClick={() => ExportToXLSX()}
                      // disabled={working}
                      >
                        <FontAwesomeIcon icon="file-excel" size="lg" />
                      </Button>
                    </Col>
                  </Row>
                  <Table striped className="data-table">
                    <thead className="table-header">
                      <tr>
                        <th>
                          <SortHeader
                            displayName="Item"
                            field="itemWithDesc"
                            sortDirection={sortDirection}
                            sorted={sortField === "itemWithDesc"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Class"
                            field="itemClass"
                            sortDirection={sortDirection}
                            sorted={sortField === "itemClass"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Avg Usage"
                            field="monthlyUsageAverage"
                            sortDirection={sortDirection}
                            sorted={sortField === "monthlyUsageAverage"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Sales Average"
                            field="monthlySalesAverage"
                            sortDirection={sortDirection}
                            sorted={sortField === "monthlySalesAverage"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Total Sold"
                            field="itemGroupTotalQuantity"
                            sortDirection={sortDirection}
                            sorted={sortField === "itemGroupTotalQuantity"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Total Sales"
                            field="itemGroupTotalSales"
                            sortDirection={sortDirection}
                            sorted={sortField === "itemGroupTotalSales"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="On Hand"
                            field="quantityOnHand"
                            sortDirection={sortDirection}
                            sorted={sortField === "quantityOnHand"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Order Date"
                            field="orderDateDisplay"
                            sortDirection={sortDirection}
                            sorted={sortField === "orderDateDisplay"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Invoice"
                            field="orderNumber"
                            sortDirection={sortDirection}
                            sorted={sortField === "orderNumber"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Customer"
                            field="customerId"
                            sortDirection={sortDirection}
                            sorted={sortField === "customerId"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Order Qty"
                            field="orderQuantity"
                            sortDirection={sortDirection}
                            sorted={sortField === "orderQuantity"}
                            callback={onSortChange}
                          />
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <SortHeader
                            displayName="Ext Price"
                            field="extendedPrice"
                            sortDirection={sortDirection}
                            sorted={sortField === "extendedPrice"}
                            callback={onSortChange}
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data && data.items.length
                        ? _.map(data.items, (datum, index) => {
                          return (
                            <tr key={index}>
                              <ItemDropdownMenu
                                itemNumber={datum.itemNumber}
                                itemDescription={datum.itemWithDesc}
                                attachmentCount={datum.itemAttachmentCount}
                              />
                              <td
                                className="data-cell"
                                children={datum.itemClass}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={helpers.formatNumberWithThousands(helpers.roundToNearestWholeNumber(
                                  datum.monthlyUsageAverage
                                ))}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={helpers.formatCurrency(
                                  datum.monthlySalesAverage
                                )}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={helpers.formatNumberWithThousands(datum.itemGroupTotalQuantity, 0)}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={helpers.formatCurrency(
                                  datum.itemGroupTotalSales
                                )}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={helpers.formatNumberWithThousands(datum.quantityOnHand)}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={datum.orderDateDisplay}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={datum.orderNumber}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={
                                  <a rel="noreferrer"
                                    href={`/customer-dashboard/${datum.customerId}`}
                                    target="_blank"
                                    className="styledAnchor"
                                  >
                                    {datum.customerId}
                                  </a>
                                }
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={datum.orderQuantity}
                                style={{ textAlign: "center" }}
                              />
                              <td
                                className="data-cell"
                                children={helpers.formatCurrency(datum.extendedPrice)}
                                style={{ textAlign: "center" }}
                              />
                            </tr>
                          );
                        })
                        : null}
                    </tbody>
                    <tfoot className="table-footer text-center">
                      <tr>
                        <td colSpan={4}></td>
                        <td className="text-bold">{helpers.formatNumberWithThousands(totals.TotalSold)}</td>
                        <td className="text-bold">{helpers.formatCurrency(totals.TotalSales)}</td>
                        <td colSpan={6}></td>
                      </tr>
                    </tfoot>
                  </Table>
                </Row>
              ) : typeOfReport.value === 1 ? <span style={{ display: 'block', textAlign: 'center', fontSize: '1.2em' }}>{firstRender ? "Please select some criteria to see results" : "No Data Found"}</span> : false}
            </ServiceThreadSpinner>
          </Fragment>
        </Col>
      </Row>
    </Container>
  );
}
